<template>
  <form @submit.prevent="handleSubmit" class="space-y-6">
    <!-- Naslov proizvoda -->
    <div>
      <label for="product-name" class="block text-sm font-medium text-gray-300"
        >Naslov oglasa *</label
      >
      <div class="mt-1">
        <input
          id="product-name"
          v-model="editableFormData.name"
          type="text"
          autocomplete="off"
          placeholder="Naziv proizvoda"
          class="appearance-none block w-full px-3 py-2 border border-gray-700 rounded-md shadow-sm placeholder-gray-500 bg-gray-700 text-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          :class="{ 'border-red-500': errors.name }"
          @blur="validateField('name')"
          @input="syncField('name')"
        />
        <p v-if="errors.name" class="mt-1 text-sm text-red-500">
          {{ errors.name }}
        </p>
      </div>
    </div>

    <!-- Kategorija -->
    <ProductCategoryDropdown
      v-model="editableFormData.categoryId"
      :error="errors.categoryId"
      @blur="validateField('categoryId')"
      @update:modelValue="syncField('categoryId')"
    />

    <!-- Cena -->
    <div>
      <label for="product-price" class="block text-sm font-medium text-gray-300"
        >Cena (RSD) *</label
      >
      <div class="mt-1 relative rounded-md shadow-sm">
        <input
          id="product-price"
          v-model="editableFormData.price"
          type="text"
          placeholder="0.00"
          autocomplete="off"
          class="appearance-none block w-full px-3 py-2 border border-gray-700 rounded-md shadow-sm placeholder-gray-500 bg-gray-700 text-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          :class="{ 'border-red-500': errors.price }"
          @blur="validateField('price')"
          @input="formatPrice"
        />
        <div
          class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
        >
          <span class="text-gray-400 sm:text-sm">RSD</span>
        </div>
        <p v-if="errors.price" class="mt-1 text-sm text-red-500">
          {{ errors.price }}
        </p>
      </div>
    </div>

    <!-- Opis -->
    <div>
      <label
        for="product-description"
        class="block text-sm font-medium text-gray-300"
        >Opis proizvoda *</label
      >
      <div class="mt-1">
        <textarea
          id="product-description"
          v-model="editableFormData.description"
          rows="5"
          placeholder="Detaljan opis proizvoda..."
          class="appearance-none block w-full px-3 py-2 border border-gray-700 rounded-md shadow-sm placeholder-gray-500 bg-gray-700 text-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          :class="{ 'border-red-500': errors.description }"
          @blur="validateField('description')"
          @input="syncField('description')"
        ></textarea>
        <p v-if="errors.description" class="mt-1 text-sm text-red-500">
          {{ errors.description }}
        </p>
      </div>
    </div>

    <!-- Slike -->
    <div>
      <label class="block text-sm font-medium text-gray-300"
        >Slike proizvoda</label
      >
      <div class="mt-2">
        <!-- Upload area - prikazuj samo ako ima manje od 12 slika -->
        <div
          v-if="editableFormData.images.length < 12"
          class="flex justify-center px-6 pt-5 pb-6 border-2 border-gray-700 border-dashed rounded-md hover:border-gray-500 transition-colors cursor-pointer"
          @click="triggerFileInput"
          @dragover.prevent
          @drop.prevent="handleDrop"
        >
          <div class="space-y-1 text-center">
            <svg
              class="mx-auto h-12 w-12 text-gray-400"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
              aria-hidden="true"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <div class="flex text-sm text-gray-400">
              <p class="pl-1">Prevucite slike ovde ili kliknite za odabir</p>
              <input
                ref="fileInput"
                type="file"
                accept="image/*"
                multiple
                class="sr-only"
                @change="handleFileChange"
              />
            </div>
            <p class="text-xs text-gray-500">PNG, JPG, GIF do 10MB</p>
            <p class="text-xs text-blue-400">
              {{ editableFormData.images.length }}/12 slika
            </p>
          </div>
        </div>

        <!-- Poruka kada je dostignut maksimum -->
        <div
          v-else
          class="flex justify-center px-6 pt-5 pb-6 border-2 border-gray-600 border-dashed rounded-md bg-gray-800"
        >
          <div class="space-y-1 text-center">
            <svg
              class="mx-auto h-12 w-12 text-gray-500"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
              aria-hidden="true"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <p class="text-sm text-gray-500">
              Dostigli ste maksimum od 12 slika
            </p>
            <p class="text-xs text-gray-500">
              Uklonite neku sliku da biste dodali novu
            </p>
            <p class="text-xs text-blue-400">
              {{ editableFormData.images.length }}/12 slika
            </p>
          </div>
        </div>

        <p v-if="errors.images" class="mt-1 text-sm text-red-500">
          {{ errors.images }}
        </p>

        <!-- Prikaz slika - prilagođeno prema ProductForm.vue -->
        <div
          v-if="editableFormData.images.length > 0"
          class="image-gallery mt-4"
        >
          <div class="image-grid">
            <div
              v-for="(image, index) in editableFormData.images"
              :key="index"
              class="image-item"
            >
              <!-- Slika sa rotacijom -->
              <div class="image-container">
                <img
                  :src="image.preview"
                  :style="{ transform: `rotate(${image.rotation || 0}deg)` }"
                  alt="Preview"
                />

                <!-- Badge za glavnu sliku -->
                <div v-if="image.isMain" class="main-badge">Glavna</div>

                <!-- Badge za postojeću sliku -->
                <div v-if="image.isExisting" class="existing-badge">
                  Postojeća
                </div>
              </div>

              <!-- Akcije -->
              <div class="image-actions">
                <div class="action-buttons">
                  <!-- Postavi kao glavnu -->
                  <button
                    v-if="!image.isMain"
                    type="button"
                    @click.stop="setMainImageWithSync(index)"
                    class="action-button blue"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
                      />
                    </svg>
                  </button>

                  <!-- Rotiraj -->
                  <button
                    type="button"
                    @click.stop="rotateImageWithSync(index)"
                    class="action-button green"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </button>

                  <!-- Ukloni -->
                  <button
                    type="button"
                    @click.stop="removeImageWithSync(index)"
                    class="action-button red"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Submit dugme i Otkaži dugme -->
    <div class="flex space-x-4">
      <button
        type="button"
        @click="cancelChanges"
        :disabled="!hasChanges"
        class="w-1/2 py-2 px-4 border border-gray-500 rounded-md shadow-sm text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:bg-gray-800 disabled:border-gray-700 disabled:text-gray-500 disabled:cursor-not-allowed"
      >
        Otkaži izmene
      </button>

      <button
        type="submit"
        :disabled="isLoading || !hasChanges"
        class="w-1/2 flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-indigo-400 disabled:cursor-not-allowed"
      >
        <svg
          v-if="isLoading"
          class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        {{
          isLoading
            ? 'Obrada...'
            : hasChanges
            ? 'Sačuvaj izmene'
            : 'Nema izmena'
        }}
      </button>
    </div>
  </form>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  computed,
  watch,
  PropType,
  onMounted,
} from 'vue';
import ProductCategoryDropdown from '@/components/ProductCategoryDropdown.vue';

// Interface za kategoriju
interface Category {
  id: string;
  name: string;
  parent?: {
    id: string;
    name: string;
  };
}

// Interface za sliku proizvoda
interface ProductImage {
  id?: string;
  path?: string;
  order: number;
  isMain: boolean;
  file?: File;
  preview?: string;
  rotation?: number;
  isExisting?: boolean; // Flag za razlikovanje postojećih i novih slika
}

// Interface za formu proizvoda
interface ProductFormData {
  id: string;
  name: string;
  description: string;
  price: string;
  categoryId: string;
  images: ProductImage[];
  imagesToDelete: string[];
  createdAt?: string;
  updatedAt?: string;
  expiresAt?: string;
  renewedAt?: string | null;
}

const MAX_IMAGES = 12; // Konstanta za maksimalnu broj slika

export default defineComponent({
  name: 'ProductEditForm',
  components: {
    ProductCategoryDropdown,
  },
  props: {
    formData: {
      type: Object as PropType<ProductFormData>,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    categories: {
      type: Array as PropType<Category[]>,
      default: () => [],
    },
  },
  emits: [
    'add-image',
    'remove-image',
    'set-main-image',
    'rotate-image',
    'update-product',
    'cancel-changes',
  ],
  setup(props, { emit }) {
    const fileInput = ref<HTMLInputElement | null>(null);
    const errors = ref<Record<string, string>>({});
    const touchedFields = ref<Set<string>>(new Set());

    // Flag koji sprečava cirkularne reaktivne pozive
    const isUpdatingFromParent = ref(false);

    // Lokalni, izmenljivi podaci forme
    const editableFormData = ref<ProductFormData>({
      id: '',
      name: '',
      description: '',
      price: '',
      categoryId: '',
      images: [],
      imagesToDelete: [],
    });

    // Originalni podaci forme za poređenje
    const originalFormData = ref<ProductFormData | null>(null);

    // Čuvamo kopiju originalnih podataka za poređenje
    onMounted(() => {
      if (props.formData) {
        // Napravimo duboki klon originalnih podataka
        originalFormData.value = JSON.parse(JSON.stringify(props.formData));
        // Inicijalno postavimo vrednosti za editableFormData
        editableFormData.value = JSON.parse(JSON.stringify(props.formData));
      }
    });

    // Ažuriranje editableFormData kada se promene props
    watch(
      () => props.formData,
      (newFormData) => {
        if (isUpdatingFromParent.value) return; // Sprečavamo kružno ažuriranje

        isUpdatingFromParent.value = true;
        try {
          if (newFormData && !originalFormData.value) {
            // Ako je prvi put, postavimo oba
            originalFormData.value = JSON.parse(JSON.stringify(newFormData));
            editableFormData.value = JSON.parse(JSON.stringify(newFormData));
          } else if (newFormData) {
            // Ažuriramo samo images da bismo sprečili cirkularne konflikte
            editableFormData.value.images = JSON.parse(
              JSON.stringify(newFormData.images)
            );
            // Ostala polja ne sync-ujemo automatski ovde, već se to radi kroz input event
          }
        } finally {
          isUpdatingFromParent.value = false;
        }
      },
      { deep: true }
    );

    // KLJUČNA IZMENA: Umesto watch-era, koristimo metode za direktnu sinhronizaciju
    const syncField = (fieldName: string) => {
      if (isUpdatingFromParent.value) return;

      isUpdatingFromParent.value = true;
      try {
        // Sinhronizujemo konkretno polje
        switch (fieldName) {
          case 'name':
            props.formData.name = editableFormData.value.name;
            break;
          case 'description':
            props.formData.description = editableFormData.value.description;
            break;
          case 'price':
            props.formData.price = editableFormData.value.price;
            break;
          case 'categoryId':
            props.formData.categoryId = editableFormData.value.categoryId;
            break;
        }
      } finally {
        isUpdatingFromParent.value = false;
      }
    };

    // Formatiranje cene sa sinhronizacijom
    const formatPrice = (event: Event) => {
      const target = event.target as HTMLInputElement;
      let value = target.value;

      // Uklonimo sve što nije broj ili tačka
      value = value.replace(/[^\d.]/g, '');

      // Uklonimo više tačaka
      const dots = value.match(/\./g);
      if (dots && dots.length > 1) {
        value = value.replace(/\./g, (match, index) =>
          index === value.indexOf('.') ? '.' : ''
        );
      }

      // Ograničimo decimale na 2
      if (value.includes('.')) {
        const parts = value.split('.');
        if (parts[1].length > 2) {
          parts[1] = parts[1].substring(0, 2);
          value = parts.join('.');
        }
      }

      editableFormData.value.price = value;

      // Sinhronizujemo nakon formatiranja
      syncField('price');
    };

    // Računamo da li ima promena u formi
    const hasChanges = computed(() => {
      if (!originalFormData.value) return false;

      // Osnovni podaci
      if (originalFormData.value.name !== editableFormData.value.name)
        return true;
      if (
        originalFormData.value.description !==
        editableFormData.value.description
      )
        return true;
      if (originalFormData.value.price !== editableFormData.value.price)
        return true;
      if (
        originalFormData.value.categoryId !== editableFormData.value.categoryId
      )
        return true;

      // Izmene vezane za slike
      if (editableFormData.value.imagesToDelete.length > 0) return true;

      // Nove slike
      if (editableFormData.value.images.some((img) => !img.isExisting))
        return true;

      // Rotacije postojećih slika
      if (
        editableFormData.value.images.some(
          (img) => img.isExisting && img.rotation && img.rotation > 0
        )
      )
        return true;

      // Promene u redosledu slika
      const originalImageOrder = originalFormData.value.images
        .map((img) => img.id)
        .join(',');
      const currentImageOrder = editableFormData.value.images
        .map((img) => img.id)
        .join(',');
      if (originalImageOrder !== currentImageOrder) return true;

      return false;
    });

    // Sinhronizovani handleri za operacije sa slikama
    const addImageWithSync = (file: File) => {
      // Prvo sinhronizujemo tekuća polja forme
      syncFormData();
      emit('add-image', file);
    };

    const removeImageWithSync = (index: number) => {
      // Prvo sinhronizujemo tekuća polja forme
      syncFormData();

      // Lokalno uklanjamo sliku iz niza
      const imageToRemove = editableFormData.value.images[index];

      // Ako je postojeća slika, dodaj je u listu za brisanje na serveru
      if (imageToRemove && imageToRemove.isExisting && imageToRemove.id) {
        editableFormData.value.imagesToDelete.push(imageToRemove.id);

        // Direktno ažuriramo i parent-ovo imagesToDelete polje
        props.formData.imagesToDelete.push(imageToRemove.id);
      }

      // Aktiviramo emit nakon što smo lokalno ažurirali podatke
      emit('remove-image', index);
    };

    const setMainImageWithSync = (index: number) => {
      // Prvo sinhronizujemo tekuća polja forme
      syncFormData();
      emit('set-main-image', index);
    };

    const rotateImageWithSync = (index: number) => {
      // Prvo sinhronizujemo tekuća polja forme
      syncFormData();
      emit('rotate-image', index);
    };

    // Rukovanje klikom na upload područje
    const triggerFileInput = () => {
      if (fileInput.value) {
        fileInput.value.click();
      }
    };

    // Rukovanje drag & drop
    const handleDrop = (event: DragEvent) => {
      if (event.dataTransfer?.files) {
        handleFiles(event.dataTransfer.files);
      }
    };

    // Rukovanje izborom fajlova
    const handleFileChange = (event: Event) => {
      const target = event.target as HTMLInputElement;
      if (target.files) {
        handleFiles(target.files);
      }
    };

    // Sinhronizacija svih polja
    const syncFormData = () => {
      if (isUpdatingFromParent.value) return;

      isUpdatingFromParent.value = true;
      try {
        props.formData.name = editableFormData.value.name;
        props.formData.description = editableFormData.value.description;
        props.formData.price = editableFormData.value.price;
        props.formData.categoryId = editableFormData.value.categoryId;

        // Ensure we fully replace the imagesToDelete array, not just append to it
        props.formData.imagesToDelete = [
          ...editableFormData.value.imagesToDelete,
        ];
      } finally {
        isUpdatingFromParent.value = false;
      }
    };

    // Obrada fajlova sa proverom ograničenja
    const handleFiles = (files: FileList) => {
      const currentImageCount = editableFormData.value.images.length;
      const filesToProcess = Array.from(files);

      // Proveravamo koliko slika možemo dodati
      const remainingSlots = MAX_IMAGES - currentImageCount;

      if (remainingSlots <= 0) {
        alert(`Možete imati maksimalno ${MAX_IMAGES} slika po oglasu.`);
        return;
      }

      if (filesToProcess.length > remainingSlots) {
        alert(
          `Možete dodati još samo ${remainingSlots} slika. Odabrane su prve ${remainingSlots} slika.`
        );
        // Uzimamo samo one slike koje možemo dodati
        filesToProcess.splice(remainingSlots);
      }

      filesToProcess.forEach((file) => {
        if (file.type.startsWith('image/')) {
          if (file.size <= 10 * 1024 * 1024) {
            // 10MB limit
            // Prvo sinhronizujemo polja pa onda dodajemo sliku
            syncFormData();
            emit('add-image', file);
          } else {
            alert(`Slika "${file.name}" je veća od 10MB i neće biti dodana.`);
          }
        } else {
          alert(`Fajl "${file.name}" nije slika i neće biti dodat.`);
        }
      });

      // Resetujemo input kako bi korisnik mogao ponovno izabrati iste slike
      if (fileInput.value) {
        fileInput.value.value = '';
      }

      // Validiramo polje slika ako je već bilo dodirnuto
      if (touchedFields.value.has('images')) {
        validateField('images');
      }
    };

    // Validacija pojedinačnog polja
    const validateField = async (fieldName: string) => {
      touchedFields.value.add(fieldName);

      const result = await validateForm();
      if (!result.isValid && result.errors[fieldName]) {
        errors.value[fieldName] = result.errors[fieldName];
      } else {
        delete errors.value[fieldName];
      }
    };

    // Validacija forme
    const validateForm = async (): Promise<{
      isValid: boolean;
      errors: Record<string, string>;
    }> => {
      const errors: Record<string, string> = {};

      if (!editableFormData.value.name.trim()) {
        errors.name = 'Naziv proizvoda je obavezan';
      } else if (editableFormData.value.name.length < 3) {
        errors.name = 'Naziv proizvoda mora imati najmanje 3 karaktera';
      } else if (editableFormData.value.name.length > 100) {
        errors.name = 'Naziv proizvoda ne sme biti duži od 100 karaktera';
      }

      if (!editableFormData.value.description.trim()) {
        errors.description = 'Opis proizvoda je obavezan';
      } else if (editableFormData.value.description.length < 10) {
        errors.description = 'Opis proizvoda mora imati najmanje 10 karaktera';
      }

      if (!editableFormData.value.price.trim()) {
        errors.price = 'Cena je obavezna';
      } else if (
        isNaN(parseFloat(editableFormData.value.price)) ||
        parseFloat(editableFormData.value.price) <= 0
      ) {
        errors.price = 'Cena mora biti pozitivan broj';
      }

      if (!editableFormData.value.categoryId) {
        errors.categoryId = 'Kategorija je obavezna';
      }

      // Validacija broja slika
      if (editableFormData.value.images.length > MAX_IMAGES) {
        errors.images = `Možete imati maksimalno ${MAX_IMAGES} slika po oglasu`;
      }

      return {
        isValid: Object.keys(errors).length === 0,
        errors,
      };
    };

    // Funkcija za otkazivanje izmena
    const cancelChanges = () => {
      // Vrati formu na originalnu vrednost
      if (originalFormData.value) {
        editableFormData.value = JSON.parse(
          JSON.stringify(originalFormData.value)
        );

        // Sinhronizujemo nazad sa parentom
        syncFormData();
      }

      // Resetuj greške
      errors.value = {};
      touchedFields.value = new Set();

      // Obavesti roditeljsku komponentu
      emit('cancel-changes');
    };

    // Slanje forme
    const handleSubmit = async () => {
      // Označimo sva polja kao dodirnuta
      ['name', 'description', 'price', 'categoryId'].forEach((field) => {
        touchedFields.value.add(field);
      });

      const validation = await validateForm();
      if (!validation.isValid) {
        errors.value = validation.errors;
        return;
      }

      // Sinhronizujemo sve izmene pre slanja
      syncFormData();

      // Šaljemo podatke na roditeljsku komponentu
      emit('update-product');
    };

    return {
      editableFormData,
      hasChanges,
      fileInput,
      errors,
      triggerFileInput,
      handleDrop,
      handleFileChange,
      formatPrice,
      validateField,
      handleSubmit,
      cancelChanges,
      syncField,
      syncFormData,
      setMainImageWithSync,
      rotateImageWithSync,
      removeImageWithSync,
      addImageWithSync,
    };
  },
});
</script>

<style scoped>
/* Potpuno prepravljeni stilovi za galeriju slika */
.image-gallery {
  width: 100%;
  overflow: hidden;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  width: 100%;
}

@media (min-width: 640px) {
  .image-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (min-width: 768px) {
  .image-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

.image-item {
  position: relative;
  border-radius: 0.5rem;
  border: 1px solid #374151; /* border-gray-700 */
  overflow: hidden;
  width: 100%;
}

.image-container {
  position: relative;
  padding-bottom: 100%; /* aspect ratio 1:1 */
  background-color: #1f2937; /* bg-gray-800 */
  overflow: hidden;
}

.image-container img {
  position: absolute;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.2s;
}

.main-badge {
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  background-color: #312e81; /* bg-indigo-900 */
  color: #c7d2fe; /* text-indigo-200 */
  font-size: 0.75rem;
  font-weight: bold;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
}

.existing-badge {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background-color: #065f46; /* bg-green-900 */
  color: #a7f3d0; /* text-green-200 */
  font-size: 0.75rem;
  font-weight: bold;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
}

.image-actions {
  position: absolute;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;
}

.image-item:hover .image-actions {
  opacity: 1;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
}

.action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  border-radius: 9999px;
  color: white;
}

.action-button.blue {
  background-color: #2563eb; /* bg-blue-600 */
}

.action-button.blue:hover {
  background-color: #1d4ed8; /* bg-blue-700 */
}

.action-button.green {
  background-color: #059669; /* bg-green-600 */
}

.action-button.green:hover {
  background-color: #047857; /* bg-green-700 */
}

.action-button.red {
  background-color: #dc2626; /* bg-red-600 */
}

.action-button.red:hover {
  background-color: #b91c1c; /* bg-red-700 */
}
</style>
